import m from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {Button, DataCard, Rating} from '@bitstillery/common/components/portal'
import {state} from '@bitstillery/mithril'
import {format_iso_to_date, format_iso_to_fixed_date_format, format_money_with_symbol} from '@bitstillery/common/lib/format'
import {$t, api, logger, notifier} from '@bitstillery/common/app'
import {AvailabilityType} from '@bitstillery/common/api/fact2server'

import {OrderLines} from './order_lines'
import {WarehouseStatus} from './warehouse_status'

import {OrderSummary} from '../order_summary/order_summary'

import type {OrderType} from './proforma'

import {$m, $s} from '@/app'
import {IncentivePromotion} from '@/components/incentives'

interface OrderAttrs {
    order_artkey: number
}

interface OrderData {
    feedback: {
        closed: boolean
        comment: string
        loading: boolean
        rating: number
        submitted: boolean
    }
    legacy_order: boolean
    loading: boolean
    order: OrderType | null
}

export class Order extends MithrilComponent<OrderAttrs> {
    data: OrderData = state(
        {
            feedback: {
                closed: false,
                comment: '',
                loading: true,
                rating: 0,
                submitted: false,
            },
            legacy_order: false,
            loading: true,
            order: null,
        },
        'order-data',
    )

    confirm_order = m.route.param('confirm_order') !== undefined
    is_promotion = m.route.param('is_promotion') !== undefined

    override oninit() {
        this.data.feedback.loading = false
    }

    override async oncreate(vnode: m.Vnode<OrderAttrs>) {
        const order = await $m.order.load_full_order(vnode.attrs.order_artkey)
        // A failed load answers `{}`, which has no sales order to render.
        if (!order.sales_order) {
            notifier.notify($t('notifications.order_failure', {order: `S${vnode.attrs.order_artkey}`}), 'danger', 7000)
            this.data.loading = false
            return
        }
        this.data.order = order
        const account_slug = order.sales_order.account_slug

        if ($s.portal.account.slug === account_slug) {
            logger.debug('[orders] default account, retrieving rating details')
            // There are also sales-orders here from source_from_account_artkey relations;
            // E.g. sales-orders from old accounts. These can't be rated.

            // Rating is not provided for old MSI/A2BC accounts.
            const {result: rating_result, success} = await api.get(`portal/orders/${vnode.attrs.order_artkey}/rating`)
            if (success) {
                Object.assign(this.data.feedback, {
                    closed: false,
                    comment: rating_result.comment || '',
                    rating: rating_result.rating,
                    submitted: rating_result.rating > 0,
                })
            } else {
                notifier.notify('Cannot load the order rating', 'warning')
            }
        } else {
            logger.debug(`[orders] part of source_from_account_artkey account: ${account_slug}`)
        }

        this.data.loading = false
    }

    value_details(order: OrderType) {
        const tbo_exclude = new Set(['stock', 'purchase'])
        const value_details: {label: string; value: string}[] = []
        const stock_items = order.sales_order_items.filter((i) => i.offer_item_type === AvailabilityType.Stock)
        const stock_value = stock_items.map((i) => Number(i.price_per_case) * i.number_of_cases).reduce((a, b) => a + b, 0)

        const arriving_items = order.sales_order_items.filter((i) => i.offer_item_type === AvailabilityType.Purchase)
        const arriving_value = arriving_items.map((i) => Number(i.price_per_case) * i.number_of_cases).reduce((a, b) => a + b, 0)

        if (arriving_items.length > 0) {
            value_details.push({
                label: $t('order.details.items.arriving'),
                value: format_money_with_symbol(arriving_value, order.sales_order.was_sold_in),
            })
        }

        const tbo_items = order.sales_order_tbo_items.filter((i) => !tbo_exclude.has(i.offer_item_type))
        const tbo_value = tbo_items.map((i) => Number(i.price_per_case) * i.number_of_cases).reduce((a, b) => a + b, 0)

        if (tbo_items.length > 0) {
            value_details.push({
                label: $t('order.details.items.tbo'),
                value: format_money_with_symbol(tbo_value, order.sales_order.was_sold_in),
            })
        }

        const credit_items = order.sales_order_credit_items

        if (credit_items.length > 0) {
            const credit_value =
                credit_items.map((i) => Number(i.original_price_per_case) * i.number_of_cases).reduce((a, b) => a + b, 0) * -1
            value_details.push({
                label: $t('order.details.credit.label'),
                value: format_money_with_symbol(credit_value, order.sales_order.was_sold_in),
            })
        }

        const additional_items = order.sales_order_additionals
        if (additional_items.length > 0) {
            const additional_value = order.sales_order_additionals
                .map((i) => i.quantity * Number(i.price_per_unit))
                .reduce((a, b) => a + b, 0)
            value_details.push({
                label: $t('order.details.additionals'),
                value: format_money_with_symbol(additional_value, order.sales_order.was_sold_in),
            })
        }

        if (stock_items.length > 0) {
            value_details.push({
                label: $t('order.details.items.stock'),
                value: format_money_with_symbol(stock_value, order.sales_order.was_sold_in),
            })
        }

        return value_details
    }

    async repeat_order(order: OrderType) {
        this.data.loading = true
        await api.post(`portal/orders/${order.sales_order.artkey}/repeat-order`, {}, true)
        const current_order = await $m.order.load_current_order()
        // load_current_order has told the customer already when it couldn't load the order.
        if (current_order) {
            notifier.notify($t('notifications.order_repeated', {order: current_order.sales_order.artkey}), 'info', 5000)
        }
        this.data.loading = false
        void m.route.set('/shop/checkout')
    }

    // Mirrors the status badge mapping on the orders overview so both pages agree.
    status_badge(portal_status: string): {label: string; type: string} {
        const type_map: Record<string, string> = {
            [$m.order.OrderStatus.INVOICED]: 'inactive',
            [$m.order.OrderStatus.CONFIRMED]: 'success',
            [$m.order.OrderStatus.FINALIZED]: 'warning',
        }
        return {label: $t(`orders.portal_status.${portal_status}`), type: type_map[portal_status] ?? 'info'}
    }

    // The order header carries case totals but no bottle total; derive it from the lines.
    total_bottles(order: Partial<Pick<OrderType, 'sales_order_items'>>): number {
        return (order.sales_order_items ?? []).reduce(
            (total, item) => total + item.number_of_cases * item.case_number_of_bottles,
            0,
        )
    }

    action_buttons(order: OrderType, is_cart_order: boolean): m.Vnode[] {
        const elements: m.Vnode[] = []
        if (!is_cart_order && $s.portal.account.slug === order.sales_order.account_slug) {
            elements.push(
                <Button
                    icon='cartRight'
                    onclick={() => this.repeat_order(order)}
                    text={$t('order.details.actions.repeat_order')}
                    type='info'
                    variant='context'
                />,
            )
        }
        if (order.sales_order.portal_status === $m.order.OrderStatus.INVOICED) {
            elements.push(
                <Button
                    icon='invoice'
                    onclick={async () => {
                        const filename = `Invoice - ${order.sales_order.reference} - ${order.invoice_details.supplier.name}.pdf`
                        await api.download(`portal/orders/${order.sales_order.artkey}/invoice`, filename)
                    }}
                    text={$t('order.details.actions.label.invoice')}
                    tip={$t('order.details.actions.download_invoice')}
                    type='info'
                    variant='context'
                />,
                <Button
                    icon='delivery'
                    onclick={async () => {
                        const filename = `Packing list - ${order.sales_order.reference} - ${order.invoice_details.supplier.name}.pdf`
                        await api.download(`portal/orders/${order.sales_order.artkey}/outtake`, filename)
                    }}
                    text={$t('order.details.actions.label.delivery_note')}
                    tip={$t('order.details.actions.download_delivery_note')}
                    type='info'
                    variant='context'
                />,
            )
        } else if ([$m.order.OrderStatus.FINALIZED, $m.order.OrderStatus.CONFIRMED].includes(order.sales_order.portal_status)) {
            elements.push(
                <Button
                    icon='pdf'
                    onclick={async () => {
                        const filename = `Proforma - ${order.sales_order.reference} - ${order.invoice_details.supplier.name}.pdf`
                        await api.download(`portal/orders/${order.sales_order.artkey}/proforma`, filename)
                    }}
                    text={$t('order.details.actions.label.proforma')}
                    tip={$t('order.details.actions.download_proforma')}
                    type='info'
                    variant='context'
                />,
                <Button
                    icon='excel'
                    onclick={async () => {
                        const file_name = `Sales order ${order.sales_order.reference} ${format_iso_to_fixed_date_format(new Date().toISOString())}.xlsx`
                        const {success} = await api.download(`portal/orders/${order.sales_order.artkey}/export`, file_name)
                        if (!success) {
                            notifier.notify('Cannot download the order note', 'warning')
                        }
                    }}
                    text={$t('order.details.actions.label.order_note')}
                    tip={$t('order.details.actions.download_order_note')}
                    type='info'
                    variant='context'
                />,
            )
        }
        return elements
    }

    view(vnode: m.Vnode<OrderAttrs>) {
        const is_cart_order = $s.cart.artkey !== null && String($s.cart.artkey) === String(vnode.attrs.order_artkey)
        const order = this.data.order

        const actions = order && !this.is_promotion ? this.action_buttons(order, is_cart_order) : []
        if (!order) return null
        return (
            <div className='c-order'>
                <div className='view'>
                    {this.confirm_order && (
                        <div className='order-confirmation'>
                            <div className='title'>{$t('order.confirmed.title')}</div>
                            <div className='subtitle'>
                                {this.is_promotion ? $t('promotions.confirmed.subtitle') : $t('order.confirmed.subtitle')}
                            </div>
                            <div className='description'>
                                {this.is_promotion ? $t('promotions.confirmed.description') : $t('order.confirmed.description')}
                                {this.is_promotion && (
                                    <Button
                                        icon='list'
                                        link={'/my-portal/promotions'}
                                        text={$t('promotions.label.show_promotion')}
                                        type='success'
                                    />
                                )}
                            </div>
                            {!this.is_promotion && $s.promotion.active && (
                                <div className='promotion-reminder'>
                                    <div className='description'>
                                        {$s.promotion.tokens_draft > 0
                                            ? $t('promotions.order_reminder_eligible')
                                            : $t('promotions.order_reminder', {token_value: $s.promotion.token_value})}
                                    </div>
                                    <IncentivePromotion />
                                </div>
                            )}
                        </div>
                    )}

                    {this.confirm_order && !this.is_promotion && !this.data.legacy_order && (
                        <Rating
                            model={this.data.feedback}
                            on_submit={async () => {
                                await api.put(`portal/orders/${vnode.attrs.order_artkey}/rating`, this.data.feedback)
                            }}
                        />
                    )}

                    <OrderSummary
                        account_slug={order.sales_order.account_slug}
                        actions={actions.length ? actions : undefined}
                        bottles={this.total_bottles(order)}
                        cases={order.sales_order.number_of_cases}
                        created_on={order.sales_order.created_on}
                        currency={order.sales_order.was_sold_in}
                        reference={$t('my_portal.options.order', {order: `S${vnode.attrs.order_artkey}`})}
                        status={this.status_badge(order.sales_order.portal_status)}
                        total_value={order.sales_order.was_sold_for}
                    />
                    <DataCard
                        type='info'
                        model={{
                            data: [
                                order.sales_order.portal_status === $m.order.OrderStatus.INVOICED
                                    ? {label: $t('order.details.invoice_number'), value: order.sales_order.invoice_number}
                                    : null,
                                order.sales_order.portal_status === $m.order.OrderStatus.INVOICED
                                    ? {
                                          label: $t('order.details.invoice_date'),
                                          value: order.sales_order.is_invoiced_on
                                              ? format_iso_to_date(order.sales_order.is_invoiced_on, $s.language)
                                              : '',
                                      }
                                    : null,
                                {
                                    label: $t('order.details.vat'),
                                    value: format_money_with_symbol(order.sales_order.vat_total, order.sales_order.was_sold_in),
                                },
                                ...this.value_details(order),
                            ],
                        }}
                    />

                    <WarehouseStatus sales_order_artkey={vnode.attrs.order_artkey} />

                    <OrderLines order={order} />
                </div>
            </div>
        )
    }
}
